<script lang="ts" setup>
import {
    ArrowRightCircleIcon,
    ArrowUturnLeftIcon,
    ClipboardDocumentIcon,
    EyeDropperIcon,
    PencilIcon,
    TrashIcon,
} from "@heroicons/vue/24/outline";
import Dropdown from "@src/components/ui/navigation/Dropdown/Dropdown.vue";
import {useChatSessions} from "@src/composables/useChatSessions"
import ChatMessage from "@src/types/ChatMessage";
import {useChatSessionStore} from "@src/stores/chatSession";
import MessageService from "@src/services/message";
import GroupMessage from "@src/types/GroupMessage";
import {computed} from "vue";

const {pinMessage} = useChatSessions()

const props = defineProps<{
    message: ChatMessage | GroupMessage;
    isGroup: boolean;
    show: boolean;
    self: boolean;
    left: number;
    top: number;
    handleCloseContextMenu: () => void;
}>();

const sessionStore = useChatSessionStore()
const emits = defineEmits(['close-context-menu'])

// (event) pin message to conversation
function handleReplyToMessage() {
    sessionStore.replyMessage = props.message;
    sessionStore.inputMode = 'normal';
    sessionStore.selectedMessage = undefined;
    emits('close-context-menu')
}

function handleForwardMessage() {
    if (!canShowForwardButton.value) return;

    sessionStore.startForwardMode(props.message);
    props.handleCloseContextMenu();
    emits('close-context-menu');
}

async function handlePin(message: ChatMessage | GroupMessage) {
    props.handleCloseContextMenu();

    const success = await pinMessage(
        sessionStore.selectedSession!.id,
        message.id!
    )

    if (success && sessionStore.selectedSession) {
        sessionStore.selectedSession.pinned_message = message as ChatMessage
        sessionStore.selectedSession.pinned_message_hidden = false
    }
}

async function deleteMessage(message: ChatMessage | GroupMessage) {
    props.handleCloseContextMenu();
    await MessageService.deletedMessage(message.id as number)
}

function editMessage(message: ChatMessage | GroupMessage) {
    sessionStore.inputMode = 'edit';
    sessionStore.selectedMessage = message as ChatMessage;
    sessionStore.replyMessage = undefined;
}

const handleCopyMessage = async () => {
    props.handleCloseContextMenu();

    try {

        if (props.message.type === 'image') {

            const imageUrl = props.isGroup
                ? props.message.content
                : props.message.message;

            const response = await fetch(imageUrl!, {mode: 'cors'});
            const blob = await response.blob();

            // 🔥 Converter para PNG usando canvas
            const img = new Image();
            img.crossOrigin = "anonymous";
            img.src = URL.createObjectURL(blob);

            await new Promise((resolve, reject) => {
                img.onload = resolve;
                img.onerror = reject;
            });

            const canvas = document.createElement("canvas");
            canvas.width = img.width;
            canvas.height = img.height;

            const ctx = canvas.getContext("2d");
            ctx!.drawImage(img, 0, 0);

            const pngBlob = await new Promise<Blob>((resolve) =>
                canvas.toBlob((b) => resolve(b!), "image/png")
            );

            await navigator.clipboard.write([
                new ClipboardItem({
                    "image/png": pngBlob
                })
            ]);

        } else if (props.message.type === 'text') {

            const text = props.isGroup
                ? props.message.content
                : props.message.message;

            await navigator.clipboard.writeText(text!);
        }

    } catch (err) {
        console.error("Erro ao copiar mensagem", err);
    }
};

const showEditButton = computed(() => {
    const {message} = props;
    const channelType = sessionStore.selectedSession?.channel?.type ?? null; // groups don't have channel

    // Canais ou remetentes que não permitem edição
    if (
        message.from === 'system' ||
        message.from === 'costumer' ||
        message.status === '5' || // mensagem apagada
        channelType === 'instagram' ||
        channelType === 'waapi' ||
        channelType === 'facebook'
    ) return false;

    // Usuario só pode editar dentro de 15 minutos
    if (message.from === 'user') {
        const diffInMs = Date.now() - new Date(message.created_at).getTime();
        return diffInMs <= 15 * 60 * 1000;
    }

    return true;
});

const showReplyButton = computed(() => {
    let allowedTypes = sessionStore.selectedSession?.channel?.type === 'whatsapp' || sessionStore.selectedSession?.channel?.type === 'waapi'
    let isSelectedSession = 'chat_session_id' in props.message && props.message.chat_session_id === sessionStore.selectedSession?.id
    let allowedSender = props.message.from !== 'system'
    return props.isGroup || (allowedSender && allowedTypes && isSelectedSession)
})

const canShowForwardButton = computed(() => {
    return props.message.type !== 'video' && props.message.status !== '5' && props.message.from !== 'system' && props.message.type !== 'warning';
});

</script>

<template>
    <!--custom context menu-->
    <Dropdown
        v-if="sessionStore.type === 'group' || ( sessionStore.selectedSession?.status === 'active' && message.from !== 'system')"
        :close-dropdown="handleCloseContextMenu"
        :coordinates="{
      left: props.left + 'px',
      top: props.top + 'px',
    }"
        :handle-click-outside="handleCloseContextMenu"
        :position="['top-0']"
        :show="show"
        :fixed="true"
    >
        <button
            v-if="showReplyButton"
            aria-label="responder esta mensagem"
            class="dropdown-link dropdown-link-primary"
            role="menuitem"
            @click="handleReplyToMessage"
        >
            <ArrowUturnLeftIcon class="h-5 w-5 mr-3"/>
            Responder
        </button>

        <button
            v-if="props.message.type === 'text'|| props.message.type === 'image'"
            aria-label="copiar esta mensagem"
            class="dropdown-link dropdown-link-primary"
            role="menuitem"
            @click="handleCopyMessage"
        >
            <ClipboardDocumentIcon class="h-5 w-5 mr-3"/>
            Copiar
        </button>

        <button
            v-if="props.isGroup || message.from !== 'system'"
            aria-label="fixar esta mensagem"
            class="dropdown-link dropdown-link-primary"
            role="menuitem"
            @click="handlePin(props.message)"
        >
            <EyeDropperIcon class="h-5 w-5 mr-3"/>
            Fixar
        </button>

        <button
            v-if="canShowForwardButton"
            aria-label="encaminhar esta mensagem"
            class="dropdown-link dropdown-link-primary"
            role="menuitem"
            @click.stop="handleForwardMessage"
        >
            <ArrowRightCircleIcon class="h-5 w-5 mr-3"/>
            Encaminhar
        </button>

        <button
            v-if="showEditButton"
            aria-label="editar esta mensagem"
            class="dropdown-link dropdown-link-danger"
            role="menuitem"
            @click="editMessage(props.message)"
        >
            <PencilIcon class="h-5 w-5 mr-3"/>
            Editar
        </button>

        <button
            v-if="showEditButton"
            aria-label="deletar esta mensagem"
            class="dropdown-link dropdown-link-danger"
            role="menuitem"
            @click="deleteMessage(props.message)"
        >
            <TrashIcon class="h-5 w-5 mr-3"/>
            Apagar
        </button>
    </Dropdown>
</template>
